import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, RefreshCw, GitMerge, AlertTriangle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useAppContext } from '@/contexts/AppContext';
import {
  PAYOUT_COLUMNS,
  loadPayoutColumnMap,
  savePayoutColumnMap,
} from '@/lib/settingsConfig';

const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';
const FLAT = '__flat__';

const PayoutColumnMappingManager: React.FC = () => {
  const { paymentTypes } = useAppContext() as any;
  const [map, setMap] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const ptNames: string[] = useMemo(
    () => (paymentTypes || []).map((pt: any) => pt.name).filter(Boolean),
    [paymentTypes],
  );

  const load = async () => {
    setLoading(true);
    const m = await loadPayoutColumnMap(DEFAULT_TENANT_ID);
    setMap(m);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const setColumn = (col: string, value: string) => {
    setMap((prev) => ({ ...prev, [col]: value === FLAT ? null : value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await savePayoutColumnMap(map, DEFAULT_TENANT_ID);
      toast({
        title: 'Mapping saved',
        description: 'Payout columns are now linked to payment types. Reports and the driver modal will use this on next load.',
      });
    } catch (e: any) {
      toast({ title: 'Not saved', description: e?.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="border-b border-border">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-xl text-primary flex items-center gap-2">
              <GitMerge className="w-5 h-5" />
              Payout Column → Payment Type Mapping
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Link each weekly-payout turnover column to a payment type. The payout report and driver
              modal read these to pick the right commission %, so renaming a payment type (e.g. Credit
              Card → EFTPOS) will not silently break commission.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button onClick={save} disabled={saving} className="golden-button">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Mapping'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PAYOUT_COLUMNS.map((col) => {
            const current = map[col.key];
            const selectValue = current == null ? FLAT : current;
            const missing = current != null && ptNames.length > 0 && !ptNames.includes(current);
            return (
              <div key={col.key} className="rounded-lg border border-border bg-background/40 p-4">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground">{col.label}</Label>
                  <span className="text-xs text-muted-foreground">{col.key}</span>
                </div>
                <Select value={selectValue} onValueChange={(v) => setColumn(col.key, v)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select payment type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={FLAT}>— Flat company commission % —</SelectItem>
                    {/* keep the current value selectable even if it no longer exists */}
                    {missing && <SelectItem value={current as string}>{current} (missing)</SelectItem>}
                    {ptNames.map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {missing && (
                  <p className="mt-2 flex items-center gap-1 text-xs text-amber-500">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    "{current}" no longer exists as a payment type. Pick the renamed one to fix commission.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="rounded-lg bg-blue-950/30 border border-blue-900/40 p-3">
          <p className="text-sm text-blue-300">
            <strong>Tip:</strong> "Flat company commission %" means that column uses the company default
            commission rate (Payment Settings) rather than a payment-type-specific rate.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PayoutColumnMappingManager;
